/* Art House bag: shared, serialized Shopify cart state in an accessible modal. */
(() => {
  'use strict';
  if (window.AlawooderCartDrawer) { window.AlawooderCartDrawer.mountAll(); return; }
  const instances = new Map();
  function init(drawer) {
  if (instances.has(drawer)) return;
  const theme = window.AWTheme;
  if (!drawer || !theme?.cart || typeof drawer.showModal !== 'function') return;
  const config = window.Alawooder || {};
  const controller = new AbortController();
  const on = (target,type,handler,options = {}) => target.addEventListener(type,handler,{...options,signal:controller.signal});
  const defaults = {"eyebrow": "Your selection", "heading": "The bag.", "close_label": "Close bag", "loading_label": "Loading your bag…", "empty_eyebrow": "A place reserved", "empty_heading": "A little room.\nfor something good.", "empty_text": "Your bag is empty. Find a piece for the way you live.", "explore_label": "Continue exploring", "subtotal_label": "Subtotal", "checkout_note": "Taxes and shipping calculated at checkout.", "checkout_label": "Checkout", "full_bag_label": "View full bag", "refresh_label": "Refresh bag", "view_item_label": "View {title}", "decrease_label": "Decrease quantity for {title}", "increase_label": "Increase quantity for {title}", "quantity_label": "Quantity for {title}", "minus_label": "−", "plus_label": "+", "remove_label": "Remove", "remove_item_label": "Remove {title}", "read_error": "We could not read your bag.", "refreshing": "Refreshing your bag…", "temporary_unavailable": "Your bag is temporarily unavailable.", "refresh_failed": "We could not refresh your bag. Try refreshing, or view your full bag below.", "quantity_invalid": "Choose a quantity of at least {min}{maximum}, in multiples of {step}.", "quantity_maximum": " and no more than {max}", "removing": "Removing this piece…", "updating": "Updating your bag…", "changed": "Your bag changed. Please check the updated selection.", "confirm_failed": "We could not confirm this update. Check your bag before trying again.", "update_quantity_failed": "We could not update this quantity. Please check availability.", "limited_quantity": "We could only keep {quantity} of this piece in your bag. Please review the updated quantity.", "not_available": "This piece is no longer available in your bag. Please review your selection.", "removed": "Piece removed from your bag.", "updated": "Your bag is updated.", "update_failed": "We could not update your bag.", "added": "Added to your bag."};
  let strings = defaults;
  try { strings = {...defaults,...JSON.parse(drawer.querySelector('[data-aw-cart-drawer-copy]')?.textContent || '{}')}; } catch (_) {}
  const text = (key,values = {}) => String(strings[key] ?? defaults[key] ?? '').replace(/\{(\w+)\}/g,(token,name) => values[name] === undefined ? token : String(values[name]));
  let disposed = false;
  const items = drawer.querySelector('[data-aw-cart-drawer-items]');
  const empty = drawer.querySelector('[data-aw-cart-drawer-empty]');
  const status = drawer.querySelector('[data-aw-cart-drawer-status]');
  const footer = drawer.querySelector('[data-aw-cart-drawer-footer]');
  const subtotal = drawer.querySelector('[data-aw-cart-drawer-subtotal]');
  const count = drawer.querySelector('[data-aw-cart-drawer-count]');
  const checkout = drawer.querySelector('[data-aw-cart-checkout]');
  const discounts = drawer.querySelector('[data-aw-cart-discounts]');
  if (![items,empty,status,footer,subtotal,count,checkout,discounts].every(Boolean) || !drawer.querySelector('form')) return;
  const submittedAt = new WeakMap();
  let cart = null;
  let ready = false;
  let busy = false;
  let dismissals = 0;
  let previousFocus = null;
  let closeTimer;

  function node(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = String(text);
    return element;
  }

  function safeUrl(value, sameOrigin = false) {
    if (!value) return '';
    try {
      const url = new URL(value, location.origin);
      if (!/^https?:$/.test(url.protocol) || (sameOrigin && url.origin !== location.origin)) return '';
      return url.href;
    } catch (error) { return ''; }
  }

  function message(content = '', error = false, retry = false) {
    if (disposed) return;
    status.textContent = content;
    status.dataset.state = error ? 'error' : 'success';
    status.setAttribute('role', error ? 'alert' : 'status');
    status.setAttribute('aria-live', error ? 'assertive' : 'polite');
    if (retry) {
      const button = node('button', 'aw-cart-drawer-retry', text('refresh_label'));
      button.type = 'button';
      button.dataset.awCartRetry = '';
      status.append(document.createTextNode(' '), button);
    }
    if (content) theme.announce(content);
  }

  function setBusy(value) {
    if (disposed) return;
    busy = value;
    const locked = value || theme.cart.pending > 0;
    drawer.setAttribute('aria-busy', String(locked));
    items.querySelectorAll('[data-aw-cart-control]').forEach(control => {
      control.disabled = locked || control.dataset.awCartLimit === 'true';
    });
    checkout.disabled = locked || !ready || !cart?.item_count;
  }

  function rules(item) {
    const rule = item.quantity_rule || {};
    return {
      min: Math.max(1, Number(rule.min) || 1),
      step: Math.max(1, Number(rule.increment) || 1),
      max: rule.max === null || rule.max === undefined ? Infinity : Number(rule.max)
    };
  }

  function control(label, text, action, limited = false) {
    const button = node('button', '', text);
    button.type = 'button';
    button.setAttribute('aria-label', label);
    button.dataset.awCartControl = '';
    button.dataset.awCartAction = action;
    button.dataset.awCartLimit = String(limited);
    return button;
  }

  function renderLine(item, index) {
    const title = item.product_title || item.title;
    const row = node('article', 'aw-cart-line');
    row.dataset.awCartKey = item.key;
    row.dataset.awCartIndex = String(index);
    const url = safeUrl(item.url, true);
    const imageLink = node(url ? 'a' : 'div', 'aw-cart-line-image');
    if (url) { imageLink.href = url; imageLink.setAttribute('aria-label', text('view_item_label',{title})); }
    const source = safeUrl(item.featured_image?.url || item.image || '');
    if (source) {
      const image = node('img');
      image.src = source;
      image.alt = item.featured_image?.alt || title;
      image.width = 100;
      image.height = 110;
      image.loading = 'lazy';
      imageLink.append(image);
    }
    const content = node('div', 'aw-cart-line-content');
    const heading = node('h3', 'aw-cart-line-heading');
    const titleLink = node(url ? 'a' : 'span', 'aw-cart-line-title', title);
    if (url) titleLink.href = url;
    heading.append(titleLink);
    content.append(heading);
    if (!item.product_has_only_default_variant) {
      (item.options_with_values || []).forEach(option => {
        content.append(node('p', 'aw-cart-line-options', `${option.name}: ${option.value}`));
      });
    }
    if (item.selling_plan_allocation?.selling_plan?.name) content.append(node('p', 'aw-cart-line-options', item.selling_plan_allocation.selling_plan.name));
    Object.entries(item.properties || {}).forEach(([key, value]) => {
      if (key.startsWith('_') || value === null || value === '') return;
      content.append(node('p', 'aw-cart-line-options', `${key}: ${value}`));
    });
    const price = node('p', 'aw-cart-line-price');
    if (item.original_line_price > item.final_line_price) price.append(node('s', 'aw-cart-line-compare', theme.formatMoney(item.original_line_price)));
    price.append(document.createTextNode(theme.formatMoney(item.final_line_price)));
    content.append(price);
    (item.line_level_discount_allocations || []).forEach(discount => {
      content.append(node('p', 'aw-cart-line-options', `${discount.discount_application.title} (−${theme.formatMoney(discount.amount)})`));
    });
    const { min, step, max } = rules(item);
    const controls = node('div', 'aw-cart-line-controls');
    const quantity = node('div', 'aw-cart-quantity');
    const minus = control(text('decrease_label',{title}), text('minus_label'), 'decrease', item.quantity - step < min);
    const input = node('input');
    input.type = 'number';
    input.inputMode = 'numeric';
    input.value = String(item.quantity);
    input.min = String(min);
    input.step = String(step);
    if (Number.isFinite(max)) input.max = String(max);
    input.setAttribute('aria-label', text('quantity_label',{title}));
    input.dataset.awCartControl = '';
    input.dataset.awCartQuantity = '';
    const plus = control(text('increase_label',{title}), text('plus_label'), 'increase', item.quantity + step > max);
    quantity.append(minus, input, plus);
    const remove = control(text('remove_item_label',{title}), text('remove_label'), 'remove');
    remove.className = 'aw-cart-line-remove';
    controls.append(quantity, remove);
    content.append(controls);
    row.append(imageLink, content);
    return row;
  }

  function render(nextCart) {
    if (!nextCart || !Array.isArray(nextCart.items)) throw new Error(text('read_error'));
    if (disposed) { theme.cart.updateCount(nextCart); return; }
    cart = nextCart;
    ready = true;
    items.replaceChildren(...cart.items.map(renderLine));
    items.hidden = cart.items.length === 0;
    empty.hidden = cart.items.length !== 0;
    footer.hidden = cart.items.length === 0;
    count.textContent = String(cart.item_count);
    subtotal.textContent = `${theme.formatMoney(cart.total_price)} ${cart.currency || config.currency || 'USD'}`;
    discounts.replaceChildren(...(cart.cart_level_discount_applications || []).map(discount => node('li', '', `${discount.title} (−${theme.formatMoney(discount.total_allocated_amount)})`)));
    discounts.hidden = !discounts.children.length;
    theme.cart.updateCount(cart);
    setBusy(busy);
  }

  function isAvailable() {
    if (disposed || !drawer.isConnected || drawer.hidden) return false;
    for (let ancestor = drawer.parentElement; ancestor; ancestor = ancestor.parentElement) {
      if (getComputedStyle(ancestor).display === 'none') return false;
    }
    return true;
  }

  function open(trigger) {
    if (!isAvailable() || drawer.open) return;
    clearTimeout(closeTimer);
    delete drawer.dataset.awCartClosing;
    previousFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
    drawer.showModal();
    document.documentElement.classList.add('aw-cart-is-open');
  }

  function close() {
    if (disposed || !drawer.open || drawer.hasAttribute('data-aw-cart-closing')) return;
    dismissals += 1;
    drawer.dataset.awCartClosing = '';
    closeTimer = setTimeout(() => { if (!disposed) drawer.close(); }, matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 200);
  }

  on(drawer, 'close', () => {
    delete drawer.dataset.awCartClosing;
    document.documentElement.classList.remove('aw-cart-is-open');
    if (previousFocus?.isConnected && !previousFocus.disabled) previousFocus.focus({ preventScroll: true });
  });
  on(drawer, 'cancel', event => { event.preventDefault(); close(); });
  on(drawer, 'click', event => {
    if (event.target === drawer) close();
    if (event.target.closest('[data-aw-cart-close]')) close();
    if (event.target.closest('[data-aw-cart-retry]')) refresh();
    const button = event.target.closest('[data-aw-cart-action]');
    if (!button || button.disabled || busy || theme.cart.pending > 0) return;
    const row = button.closest('[data-aw-cart-key]');
    const item = cart?.items.find(line => line.key === row.dataset.awCartKey);
    if (!item) return;
    const action = button.dataset.awCartAction;
    const quantity = action === 'remove' ? 0 : item.quantity + (action === 'increase' ? 1 : -1) * rules(item).step;
    change(row, quantity, action);
  });
  on(drawer, 'change', event => {
    if (!event.target.matches('[data-aw-cart-quantity]') || busy || theme.cart.pending > 0) return;
    change(event.target.closest('[data-aw-cart-key]'), Number(event.target.value), 'input');
  });
  on(drawer, 'keydown', event => {
    if (event.key === 'Tab') {
      const focusable = [...drawer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])')].filter(element => element.getClientRects().length);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
      return;
    }
    if (event.key !== 'Enter' || !event.target.matches('[data-aw-cart-quantity]')) return;
    event.preventDefault();
    if (!busy && theme.cart.pending === 0) change(event.target.closest('[data-aw-cart-key]'), Number(event.target.value), 'input');
  });
  on(drawer.querySelector('form'), 'submit', event => {
    if (busy || theme.cart.pending > 0 || !ready || !cart?.item_count) event.preventDefault();
  });

  function focusControl(index, action) {
    if (disposed || !drawer.open || drawer.hasAttribute('data-aw-cart-closing')) return;
    const rows = items.querySelectorAll('[data-aw-cart-key]');
    const row = rows[Math.min(index, rows.length - 1)];
    const target = row?.querySelector(action === 'input' ? '[data-aw-cart-quantity]' : `[data-aw-cart-action="${action}"]`);
    (target && !target.disabled ? target : row?.querySelector('[data-aw-cart-quantity]') || drawer.querySelector('[data-aw-cart-close]')).focus({ preventScroll: true });
  }

  async function refresh() {
    if (disposed || busy) return;
    setBusy(true);
    message(text('refreshing'));
    await theme.cart.enqueue(async () => {
      if (disposed) return;
      try { render(await theme.cart.read()); message(); }
      catch (error) {
        if (disposed) return;
        ready = false;
        if (!cart) { items.replaceChildren(node('p', 'aw-cart-drawer-loading', text('temporary_unavailable'))); items.hidden = false; empty.hidden = true; }
        footer.hidden = false;
        message(text('refresh_failed'), true, true);
      } finally { setBusy(false); }
    });
  }

  async function change(row, quantity, action) {
    if (disposed || busy || theme.cart.pending > 0) return;
    const key = row.dataset.awCartKey;
    const index = Number(row.dataset.awCartIndex);
    const item = cart?.items.find(line => line.key === key);
    if (!item) return;
    const { min, step, max } = rules(item);
    if (!Number.isInteger(quantity) || (quantity !== 0 && (quantity < min || quantity > max || (quantity - min) % step !== 0))) {
      row.querySelector('input').value = String(item.quantity);
      message(text('quantity_invalid',{min,max,step,maximum:Number.isFinite(max) ? text('quantity_maximum',{max}) : ''}), true);
      return;
    }
    if (quantity === item.quantity) return;
    setBusy(true);
    message(quantity === 0 ? text('removing') : text('updating'));
    await theme.cart.enqueue(async () => {
      try {
        // A preceding add or discount can replace line keys. Refresh before changing a row.
        const latest = await theme.cart.read();
        const latestItem = latest.items.find(line => line.key === key);
        if (!latestItem) { render(latest); message(text('changed')); return; }
        const requested = action === 'increase' || action === 'decrease' ? latestItem.quantity + (action === 'increase' ? 1 : -1) * rules(latestItem).step : quantity;
        const url = new URL(config.routes?.cartChange || `${config.routes?.root || '/'}cart/change`, location.origin);
        url.pathname = url.pathname.replace(/\.(?:js|json)$/i, '').replace(/\/$/, '') + '.js';
        const response = await fetch(url, { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity: requested }) });
        let result;
        try { result = await response.json(); }
        catch (parseError) { throw new Error(text('confirm_failed')); }
        if (!response.ok || result.status >= 400 || result.errors) throw new Error(typeof result.description === 'string' ? result.description : text('update_quantity_failed'));
        render(result);
        const identity = line => JSON.stringify([line.variant_id, line.properties || {}, line.selling_plan_allocation?.selling_plan?.id || null]);
        const actual = result.items.find(line => line.key === key) || result.items.find(line => identity(line) === identity(latestItem));
        if (requested > 0 && (actual?.quantity || 0) !== requested) message(actual?.quantity ? text('limited_quantity',{quantity:actual.quantity}) : text('not_available'), true);
        else message(quantity === 0 ? text('removed') : text('updated'));
        (disposed ? document : drawer).dispatchEvent(new CustomEvent('aw:cart-updated', { bubbles: true, detail: { cart: result, source: 'cart-drawer' } }));
      } catch (error) {
        try { render(await theme.cart.read()); }
        catch (refreshError) { ready = false; }
        message(error instanceof TypeError ? text('confirm_failed') : error.message || text('update_failed'), true, !ready);
      } finally { setBusy(false); }
    });
    focusControl(index, action);
  }

  on(document, 'click', event => {
    const trigger = event.target.closest('[data-aw-cart-open], .ah-bag');
    if (!isAvailable() || !trigger || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button || document.querySelector('.ah-cart-page')) return;
    event.preventDefault();
    open(trigger);
    refresh();
  });
  on(document, 'aw:cart-busy', event => {
    if (event.detail?.busy && event.target instanceof HTMLFormElement) submittedAt.set(event.target, dismissals);
  });
  on(document, 'aw:cart-queue', () => setBusy(busy));
  function afterAdd(event) {
    if (disposed) return;
    const detail = event.detail;
    if (detail?.source === 'cart-drawer') { if (event.target !== drawer && detail.cart) render(detail.cart); return; }
    if (detail?.source !== 'product-form') return;
    if (detail.cart) render(detail.cart);
    const dismissed = submittedAt.get(detail.form) !== dismissals;
    if (!dismissed && !drawer.hasAttribute('data-aw-cart-closing')) open(detail.trigger || detail.form?.querySelector('[data-aw-add]'));
    if (detail.error) {
      message(detail.error, true, !detail.cart);
      if (!detail.cart) {
        ready = false;
        if (!cart) { items.replaceChildren(node('p', 'aw-cart-drawer-loading', text('temporary_unavailable'))); items.hidden = false; empty.hidden = true; }
        footer.hidden = false;
        setBusy(busy);
      }
    }
    else if (detail.cart) message(text('added'));
    else if (drawer.open) refresh();
  }
  on(document, 'aw:cart-updated', afterAdd);
  on(document, 'aw:cart-error', afterAdd);
  instances.set(drawer,() => {
    disposed = true;
    controller.abort();
    clearTimeout(closeTimer);
    delete drawer.dataset.awCartClosing;
    if (drawer.open) drawer.close();
    document.documentElement.classList.remove('aw-cart-is-open');
  });
  }

  function mountAll() {
    const candidates = [...document.querySelectorAll('[data-aw-cart-drawer]')];
    const target = candidates.find(node => node.dataset.awCartSection) || candidates[0];
    instances.forEach((dispose,root) => {
      if (root !== target || !root.isConnected) { dispose(); instances.delete(root); }
    });
    if (target) init(target);
  }
  window.AlawooderCartDrawer = {mountAll};
  mountAll();
  document.addEventListener('shopify:section:load',mountAll);
  document.addEventListener('shopify:section:unload',event => {
    instances.forEach((dispose,root) => {
      if (event.target === root || event.target.contains(root)) { dispose(); instances.delete(root); }
    });
    requestAnimationFrame(mountAll);
  });
})();
